import React from 'react';
import styled from 'styled-components';
import Breadcrumbs from '../components/Breadcrumbs';

const PageContainer = styled.div`
  padding-top: 80px;
  padding-bottom: 4rem;
`;

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 2rem;
  text-align: center;
`;

const Section = styled.section`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 1rem;
`;

const Text = styled.p`
  color: #64748b;
  line-height: 1.7;
  margin-bottom: 1rem;
  font-size: 1.1rem;
`;

const List = styled.ul`
  list-style-type: disc;
  margin-left: 1.5rem;
  margin-bottom: 1rem;
  color: #64748b;
  
  li {
    margin-bottom: 0.5rem;
    line-height: 1.7;
    font-size: 1.1rem;
  }
`;

function TermsOfService() {
  return (
    <PageContainer>
      <Breadcrumbs items={[{ label: 'Terms of Service', path: '/terms' }]} />
      <ContentWrapper>
        <Title>Terms of Service</Title>
        
        <Section>
          <SectionTitle>1. Acceptance of Terms</SectionTitle>
          <Text>
            By accessing and using Revguard's services, you acknowledge that you have read,
            understood, and agree to be bound by these Terms of Service. If you do not agree
            with any part of these terms, you may not use our services.
          </Text>
        </Section>

        <Section>
          <SectionTitle>2. Description of Service</SectionTitle>
          <Text>
            Revguard provides AI-powered testing solutions for Shopify stores, including but
            not limited to:
          </Text>
          <List>
            <li>Automated testing of e-commerce functionality</li>
            <li>Performance monitoring and optimization</li>
            <li>Security vulnerability assessments</li>
            <li>Cross-browser and mobile compatibility testing</li>
            <li>Integration testing with third-party applications</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>3. User Obligations</SectionTitle>
          <Text>
            When using our services, you agree to:
          </Text>
          <List>
            <li>Provide accurate and complete information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Use the services in compliance with all applicable laws</li>
            <li>Not attempt to circumvent any security measures</li>
            <li>Not use the services for any illegal or unauthorized purpose</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>4. Intellectual Property</SectionTitle>
          <Text>
            All content, features, and functionality of our services, including but not
            limited to text, graphics, logos, and software, are the exclusive property of
            Revguard and are protected by international copyright, trademark, and other
            intellectual property laws.
          </Text>
        </Section>

        <Section>
          <SectionTitle>5. Limitation of Liability</SectionTitle>
          <Text>
            Revguard shall not be liable for any indirect, incidental, special,
            consequential, or punitive damages resulting from your use of or inability to
            use our services. This includes but is not limited to loss of revenue, profits,
            or data.
          </Text>
        </Section>

        <Section>
          <SectionTitle>6. Modifications to Service</SectionTitle>
          <Text>
            We reserve the right to modify, suspend, or discontinue any part of our
            services at any time without prior notice. We may also update these Terms of
            Service from time to time, and your continued use of our services constitutes
            acceptance of any changes.
          </Text>
        </Section>

        <Section>
          <SectionTitle>7. Termination</SectionTitle>
          <Text>
            We may terminate or suspend your access to our services immediately, without
            prior notice, for conduct that we believe violates these Terms of Service or
            is harmful to other users, us, or third parties, or for any other reason at
            our sole discretion.
          </Text>
        </Section>

        <Section>
          <SectionTitle>8. Data Retention and Deletion</SectionTitle>
          <Text>
            We retain your information for as long as necessary to provide our services and
            fulfill the purposes outlined in these Terms. You may request deletion of your
            account and associated data, subject to our legal obligations and legitimate
            business interests.
          </Text>
          <List>
            <li>Account data is retained while your account is active</li>
            <li>Testing data is stored for up to 12 months</li>
            <li>Billing information is kept according to legal requirements</li>
            <li>Backup data may be retained for up to 30 days after deletion</li>
          </List>
        </Section>

        <Section>
          <SectionTitle>9. Dispute Resolution</SectionTitle>
          <Text>
            Any disputes arising from these Terms or our services shall be resolved through:
          </Text>
          <List>
            <li>Initial informal negotiation and good faith efforts</li>
            <li>Mandatory mediation if informal resolution fails</li>
            <li>Binding arbitration as a last resort</li>
            <li>Jurisdiction in the courts of [Jurisdiction]</li>
          </List>
          <Text>
            You agree to resolve disputes individually and waive any right to participate
            in a class action lawsuit against us.
          </Text>
        </Section>

        <Section>
          <SectionTitle>10. Service Level Agreement</SectionTitle>
          <Text>
            Our service level commitments include:
          </Text>
          <List>
            <li>99.9% platform availability</li>
            <li>Maximum 4-hour response time for critical issues</li>
            <li>Regular maintenance windows with advance notice</li>
            <li>Automated backup and recovery procedures</li>
          </List>
          <Text>
            Service credits may be issued for failure to meet these commitments, subject
            to our SLA terms.
          </Text>
        </Section>

        <Section>
          <SectionTitle>11. API Usage Terms</SectionTitle>
          <Text>
            When using our APIs, you agree to:
          </Text>
          <List>
            <li>Respect rate limits and usage quotas</li>
            <li>Maintain secure API credentials</li>
            <li>Not reverse engineer or modify our APIs</li>
            <li>Implement appropriate error handling</li>
          </List>
          <Text>
            We reserve the right to modify or revoke API access for any violation of
            these terms.
          </Text>
        </Section>

        <Section>
          <SectionTitle>12. Contact Information</SectionTitle>
          <Text>
            If you have any questions about these Terms of Service, please contact us at
            support@revguard.com.
          </Text>
        </Section>
      </ContentWrapper>
    </PageContainer>
  );
}

export default TermsOfService;